import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { BUSINESS } from "@/lib/site";
import { IMAGES, img } from "@/lib/images";

/**
 * Logo spot. The real round badge logo image is loaded from cloud storage.
 * `scrolled` shifts the wordmark color for contrast over the transparent hero.
 * `tone="light"` is for the dark footer (white wordmark, pink script line).
 * `size` controls the badge: sm (mobile menu, 44px), md (header, 56px),
 * lg (footer, 96px). The badge is never cropped or boxed — it has a
 * transparent background.
 */
export function Logo({
  className,
  scrolled = false,
  tone = "dark",
  size = "md",
}: {
  className?: string;
  scrolled?: boolean;
  tone?: "light" | "dark";
  size?: "sm" | "md" | "lg";
}) {
  const light = tone === "light";
  const px = size === "sm" ? 44 : size === "lg" ? 96 : 56;
  // Display size (px) → resizer width for 2x density. Footer lg uses 96/192,
  // header md uses 56/112, mobile sm uses 44/88. Keeps the badge crisp without
  // loading the raw ~400px PNG. q_70 compresses the transparent WebP further.
  const disp2x = size === "lg" ? 192 : size === "sm" ? 88 : 112;
  return (
    <Link
      to="/"
      className={cn(
        "group inline-flex items-center gap-2.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      aria-label={`${BUSINESS.name} — home`}
    >
      <img
        src={img(IMAGES.logo, disp2x, 70)}
        srcSet={`${img(IMAGES.logo, px, 70)} ${px}w, ${img(IMAGES.logo, disp2x, 70)} ${disp2x}w`}
        sizes={`${px}px`}
        alt="Pretty in Pink Cleaning Service logo"
        width={px}
        height={px}
        className="shrink-0 rounded-full transition-transform duration-300 group-hover:scale-105"
      />
      <span className="flex flex-col leading-none">
        <span
          className={cn(
            "font-display text-base font-extrabold tracking-tight transition-colors",
            light ? "text-white" : scrolled ? "text-ink" : "text-ink",
          )}
        >
          Pretty in Pink
        </span>
        <span className="font-script -mt-0.5 text-lg text-primary">Cleaning Service</span>
      </span>
    </Link>
  );
}
